import * as THREE from 'three';
import { basic, cloth, emis, metal, std, stone } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { LIGHT_K, begin, bonesOf, done, joint, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * El Gigante dormido (era III): the titan's dream given a giant's body. A colossus of shadow-flesh half turned to
 * the rock of the shell it was dreamt from: hex scutes of the Tortoise grow over its back, a ruined watchtower of
 * the old garrison still stands on its left shoulder (its window still lit), a dead tree on its back, moss on every
 * ledge, roots for a beard, a broken shackle on its wrist and another dragging from its ankle, ragged shadow-cloth,
 * and smoke boiling off it like every nightmare.
 *
 * Asleep it sleepwalks: stony lids shut, head lolling, arms half-raised before it with the hands hanging, and the
 * seams in its rock only smoulder. `awake` (below half its health) is the same body and skeleton standing up: lids
 * open on burning eyes, every crack lit with embers from inside, embers rising off it, fists clenched.
 *
 * The rest pose of each variant is its stance (so a still model reads right); the clips (anim/creatures/giant.ts)
 * are authored as absolute poses and subtract `GIANT_REST` to get each variant's offsets.
 */

export type GiantBone =
  | 'body' | 'chest' | 'head' | 'jaw' | 'tower' | 'cloak'
  | 'armL' | 'foreL' | 'handL' | 'chain' | 'armR' | 'foreR' | 'handR' | 'rock'
  | 'legL' | 'shinL' | 'legR' | 'shinR' | 'rag';

type Rot = readonly [number, number, number];

/** Rest rotation of each bone (Euler XYZ, radians) for the sleepwalker and for the giant awake. */
export const GIANT_REST: Record<'sleep' | 'awake', Partial<Record<GiantBone, Rot>>> = {
  sleep: {
    body: [0.12, 0, 0.03],
    chest: [0.26, 0.06, 0.04],
    head: [0.0, -0.18, 0.24],
    jaw: [0.3, 0, 0],
    armL: [-1.38, 0, 0.2],
    foreL: [-0.2, 0, 0],
    handL: [0.95, 0, 0.1],
    armR: [-1.5, 0, -0.14],
    foreR: [-0.06, 0, 0],
    handR: [0.8, 0, -0.1],
    legL: [-0.14, 0, -0.04],
    shinL: [0.24, 0, 0],
    legR: [0.08, 0, 0.04],
    shinR: [0.1, 0, 0],
  },
  awake: {
    body: [0.1, 0, 0],
    chest: [0.02, 0, 0],
    head: [-0.1, 0, 0],
    jaw: [0.22, 0, 0],
    armL: [-0.32, 0, -0.34],
    foreL: [-0.6, 0, 0],
    handL: [0.12, 0, 0],
    armR: [-0.32, 0, 0.34],
    foreR: [-0.6, 0, 0],
    handR: [0.12, 0, 0],
    legL: [-0.2, 0, -0.09],
    shinL: [0.32, 0, 0],
    legR: [0.05, 0, 0.09],
    shinR: [0.2, 0, 0],
  },
};

const UP = V(0, 1, 0);

export const giant = (o?: BaseOpts & { awake?: boolean }): Model => {
  const c = begin(o, 1.05, 53);
  const { g, rng } = c;
  const awake = o?.awake === true;
  const rest = GIANT_REST[awake ? 'awake' : 'sleep'];
  const rx = (n: GiantBone): number => rest[n]?.[0] ?? 0;
  const rz = (n: GiantBone): number => rest[n]?.[2] ?? 0;
  /** A bone posed at its variant's rest rotation. */
  const bj = (parent: THREE.Object3D, at: THREE.Vector3, name: GiantBone): { p: THREE.Group; abs: THREE.Group } => {
    const j = joint(parent, at, name);
    const r = rest[name];
    if (r) j.p.rotation.set(r[0], r[1], r[2]);
    return j;
  };

  // Shadow-flesh (cool charcoal), the titan's rock (cool grey, faceted), moss, roots, rusted iron, shadow-cloth.
  const flesh = std('#2f2d35', { roughness: 0.85, bumpScale: 0.03 });
  const fleshD = std('#141217', { roughness: 1 });
  const rock = stone('#5b5a60');
  const rockD = stone('#3a3a41');
  const masonry = stone('#77777a');
  const moss = std('#3f4d2b', { roughness: 1, flatShading: true, bumpScale: 0.05 });
  const mossL = std('#6c7c3e', { roughness: 1, flatShading: true, bumpScale: 0.05 });
  const lichen = std('#9aa38a', { roughness: 1, flatShading: true, bumpScale: 0.06 });
  // The stone lids: paler than the rest so they read shut at table distance.
  const lidM = stone('#8d8c93');
  const root = std('#2b231e', { roughness: 0.95 });
  const iron = metal('#3a3533', { roughness: 0.62, metalness: 0.55 });
  const rag = cloth('#141318', { side: THREE.DoubleSide });
  const flag = cloth('#4a1914', { side: THREE.DoubleSide });
  // The one self-lit material: the seams smoulder asleep and blaze awake (the tower window and the eyes too).
  const ember = awake ? emis('#ffd0b0', '#ff4f3a', 2.6) : emis('#3b1712', '#8a2a1a', 0.55);
  const smokeM = basic('#1c1a20', 0.5);

  /** A craggy lump: an icosahedron with its corners pushed in and out (shared corners move together). */
  const crag = (detail: number, amt: number): THREE.BufferGeometry => {
    const geo = new THREE.IcosahedronGeometry(1, detail);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const seen = new Map<string, number>();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
      let d = seen.get(key);
      if (d === undefined) {
        d = 1 + (rng() - 0.5) * amt;
        seen.set(key, d);
      }
      pos.setXYZ(i, x * d, y * d, z * d);
    }
    geo.computeVertexNormals();
    return geo;
  };
  const lump = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, spin = 0.5, detail?: number): THREE.Mesh => {
    const big = detail ?? (Math.max(sx, sy, sz) > 0.3 ? 1 : 0);
    const m = M(crag(big, big ? 0.2 : 0.28), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.set((rng() - 0.5) * spin, rng() * 6.3, (rng() - 0.5) * spin);
    return m;
  };
  /** A seam: a thin jagged tube along points (they glow awake). */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.009): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p);
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V((rng() - 0.5) * 0.035, (rng() - 0.5) * 0.03, (rng() - 0.5) * 0.01)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), Math.max(4, jit.length - 2), r, 3), ember, parent);
  };
  /** Point on the front of an ellipsoid (center, radii) at (x, y), pushed out a hair. */
  const front = (x: number, y: number, cx: number, cy: number, cz: number, ax: number, ay: number, az: number, sign = 1): THREE.Vector3 => {
    const u = (x - cx) / ax, v = (y - cy) / ay;
    return V(x, y, cz + sign * az * Math.sqrt(Math.max(0.02, 1 - u * u - v * v)) * 1.06);
  };
  /** A hanging root: a thin tube that sags. */
  const rootTube = (parent: THREE.Object3D, pts: THREE.Vector3[], r: number): void => {
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), pts.length * 2, r, 3), root, parent);
  };
  /** A ragged hanging panel of cloth: top edge at `top`, `w` wide, `h` long, curving round by `bend`, facing ±Z. */
  const panel = (parent: THREE.Object3D, top: THREE.Vector3, w: number, h: number, bend: number, faceZ: number, mat: THREE.Material): THREE.Mesh => {
    const geo = new THREE.PlaneGeometry(w, h, 4, 3);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const cut: number[] = [];
    for (let i = 0; i <= 4; i++) cut.push(rng() * h * 0.38 * (i % 2 ? 1 : 0.5));
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      let y = pos.getY(i) - h / 2;
      const col = Math.round((x / w + 0.5) * 4);
      if (y < -h + 0.001) y += cut[col] ?? 0;
      const z = -bend * (x / w) ** 2 * 4 + (rng() - 0.5) * 0.02 - (y / h) * 0.02;
      pos.setXYZ(i, x, y, z * faceZ);
    }
    geo.computeVertexNormals();
    const m = M(geo, mat, parent);
    m.position.copy(top);
    return m;
  };

  /** A smooth swell of flesh (a muscle, a cheek): a low sphere, scaled. */
  const swell = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(1, 6, 4), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  /** A flat patch of lichen or moss on a surface. */
  const patch = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, s: number, rx0 = 0, rz0 = 0): void => {
    const m = M(new THREE.OctahedronGeometry(1, 0), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(s, s * 0.3, s * 0.85);
    m.rotation.set(rx0 + (rng() - 0.5) * 0.2, rng() * 6.3, rz0 + (rng() - 0.5) * 0.2);
  };

  /* ---------- legs: great columns of shadow-flesh; the rock is only patches (knees, shins), feet like boulders ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const legB = bj(g, V(k * 0.26, 1.0, 0), `leg${side}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.26, 1.04, 0), V(k * 0.28, 0.57, 0.04), 0.18, 0.13, flesh, 7);
    swell(hip, flesh, k * 0.28, 0.82, 0.04, 0.19, 0.25, 0.19);
    crack(hip, [V(k * 0.22, 0.98, 0.2), V(k * 0.26, 0.84, 0.22), V(k * 0.27, 0.68, 0.19)]);
    const shinB = bj(hip, V(k * 0.28, 0.55, 0.04), `shin${side}`);
    const lo = shinB.abs;
    lump(lo, rock, k * 0.28, 0.55, 0.13, 0.12, 0.11, 0.08);
    patch(lo, lichen, k * 0.29, 0.6, 0.18, 0.05, 1.2);
    limb(lo, V(k * 0.28, 0.56, 0.04), V(k * 0.28, 0.14, 0.0), 0.13, 0.11, flesh, 7);
    swell(lo, flesh, k * 0.28, 0.43, -0.04, 0.14, 0.19, 0.14);
    lump(lo, rockD, k * 0.3, 0.32, 0.1, 0.07, 0.13, 0.05);
    // The foot: a slab of rock with stubs of toes, moss on its back.
    lump(lo, rockD, k * 0.28, 0.08, 0.07, 0.16, 0.085, 0.23);
    for (let i = -1; i <= 1; i++) {
      const toe = M(new THREE.ConeGeometry(0.04, 0.09, 4), rock, lo);
      toe.position.set(k * 0.28 + i * 0.07, 0.05, 0.28);
      toe.rotation.x = Math.PI / 2;
    }
    patch(lo, mossL, k * 0.27, 0.15, 0.04, 0.09);
    if (k > 0) {
      // A shackle on the right ankle, its chain dragged behind on the ground.
      const sh = M(new THREE.TorusGeometry(0.125, 0.026, 4, 10), iron, lo);
      sh.rotation.x = Math.PI / 2;
      sh.position.set(k * 0.28, 0.2, 0.0);
      const run = [V(0.3, 0.13, -0.15), V(0.32, 0.04, -0.21), V(0.35, 0.025, -0.29), V(0.39, 0.025, -0.36)];
      run.forEach((q, i) => {
        const l = M(new THREE.TorusGeometry(0.036, 0.012, 3, 5), iron, lo);
        l.position.copy(q);
        l.rotation.set(i < 2 ? 0.6 : Math.PI / 2, i * 0.25, i % 2 ? Math.PI / 2 : 0);
      });
    }
    legs[`leg${side}`] = legB.p;
    legs[`shin${side}`] = shinB.p;
  }

  /* ---------- body: the hips, a belt of roots and tattered shadow-cloth ---------- */
  const bodyB = bj(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  swell(body, flesh, 0, 1.08, 0, 0.36, 0.22, 0.29);
  const belt = M(new THREE.TorusGeometry(0.35, 0.036, 3, 10), root, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.12, 0.01);
  belt.scale.set(1.04, 0.86, 1);
  // Strips of cloth hang from the belt all round, with gaps between them (no apron: the legs show through).
  const ragB = bj(body, V(0, 1.06, 0.27), 'rag');
  ragB.p.rotation.x = -rx('body');
  for (const [x, w, h] of [[-0.11, 0.1, 0.55], [0.02, 0.09, 0.42], [0.13, 0.08, 0.5]] as const) {
    const st = panel(ragB.abs, V(x, 1.1, 0.3), w, h, 0.0, 1, rag);
    st.rotation.set(-0.05, (rng() - 0.5) * 0.3, (rng() - 0.5) * 0.15);
  }
  for (let i = 0; i < 5; i++) {
    const a = 1.0 + (i / 4) * (Math.PI * 2 - 2.0);
    const st = panel(body, V(Math.sin(a) * 0.34, 1.1, Math.cos(a) * 0.27), 0.13 + rng() * 0.05, 0.36 + rng() * 0.2, 0.01, 1, rag);
    st.rotation.set(Math.cos(a) * 0.12, a, -Math.sin(a) * 0.12);
  }

  /* ---------- chest: a man's barrel chest of shadow-flesh, patched with rock; the Tortoise's shell on its back ---------- */
  const chestB = bj(body, V(0, 1.3, -0.02), 'chest');
  const chest = chestB.abs;
  swell(chest, flesh, 0, 1.4, 0.05, 0.37, 0.27, 0.3);
  swell(chest, flesh, 0, 1.68, 0.0, 0.5, 0.34, 0.36);
  swell(chest, flesh, 0, 1.9, -0.04, 0.33, 0.13, 0.24);
  for (const k of [-1, 1]) swell(chest, flesh, k * 0.19, 1.74, 0.24, 0.2, 0.14, 0.12);
  // Rock grown over one breast and the flank, lichen on it.
  lump(chest, rock, -0.2, 1.76, 0.3, 0.17, 0.13, 0.08);
  patch(chest, lichen, -0.24, 1.82, 0.36, 0.06, 1.3);
  // Seams of the heart: out from the breastbone.
  const heart = V(0.02, 1.62, 0.37);
  for (let i = 0; i < 3; i++) {
    const a = -0.6 + i * 2.1;
    const pts: THREE.Vector3[] = [heart];
    for (let j = 1; j < 4; j++) {
      const r = j * 0.1;
      pts.push(front(Math.cos(a) * r * 1.3, 1.62 + Math.sin(a) * r * 0.9, 0, 1.68, 0.0, 0.5, 0.34, 0.36));
    }
    crack(chest, pts, 0.01);
  }
  // The shell: a dome of rock with hex scutes, mossed at the top, roots hanging off its rim.
  lump(chest, rockD, 0, 1.66, -0.25, 0.48, 0.42, 0.24, 0.5, 0);
  const scute = (x: number, y: number, z: number, r: number, n: THREE.Vector3): void => {
    const s = G(chest, x, y, z);
    s.quaternion.setFromUnitVectors(UP, n.normalize());
    s.rotateY(rng() * 0.3);
    M(new THREE.CylinderGeometry(r * 0.88, r, 0.08, 6), rock, s);
  };
  scute(0, 1.74, -0.46, 0.17, V(0, 0.15, -1));
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    const ox = Math.cos(a), oy = Math.sin(a);
    scute(ox * 0.31, 1.72 + oy * 0.27, -0.37, 0.15, V(ox * 0.8, 0.2 + oy * 0.7, -1));
  }
  lump(chest, moss, -0.05, 2.0, -0.36, 0.24, 0.06, 0.14);
  patch(chest, mossL, 0.0, 2.04, -0.33, 0.11);
  patch(chest, lichen, 0.28, 1.6, -0.47, 0.07, -1.2);
  patch(chest, lichen, -0.2, 1.5, -0.48, 0.05, -1.3);
  for (let i = 0; i < 2; i++) {
    const x = -0.22 + i * 0.44 + (rng() - 0.5) * 0.06;
    const z = -0.3 - Math.cos(x * 2) * 0.06;
    const len = 0.35 + rng() * 0.3;
    rootTube(chest, [V(x, 1.45, z), V(x + (rng() - 0.5) * 0.08, 1.45 - len * 0.5, z - 0.05), V(x + (rng() - 0.5) * 0.12, 1.45 - len, z - 0.02)], 0.014);
  }
  // A dead tree growing out of the shell, leaning back over its right shoulder.
  const tree = G(chest, 0.2, 1.98, -0.46);
  tree.rotation.set(-0.45, 0.3, -0.35);
  limb(tree, V(0, -0.05, 0), V(0.02, 0.32, 0), 0.04, 0.025, root, 5);
  limb(tree, V(0.02, 0.3, 0), V(-0.04, 0.5, 0.02), 0.025, 0.012, root, 4);
  limb(tree, V(0.01, 0.18, 0), V(0.14, 0.34, 0.03), 0.018, 0.008, root, 4);
  // Shadow-cloth hanging off the shell's rim, behind (it hangs plumb).
  const cloakB = bj(chest, V(0, 1.5, -0.4), 'cloak');
  cloakB.p.rotation.x = -0.75 * (rx('body') + rx('chest'));
  panel(cloakB.abs, V(0, 1.52, -0.42), 0.7, 0.85, 0.16, -1, rag);

  /* ---------- the watchtower on the left shoulder: a ruin of the old garrison, its window still lit ---------- */
  const towerB = bj(chest, V(-0.46, 2.0, -0.08), 'tower');
  const tw = towerB.abs;
  lump(tw, rock, -0.48, 2.0, -0.08, 0.17, 0.1, 0.17);
  const T = G(tw, -0.5, 2.04, -0.1);
  T.rotation.set(-0.12, 0.4, 0.22);
  M(new THREE.CylinderGeometry(0.1, 0.125, 0.36, 8, 1, true), masonry, T).position.y = 0.17;
  M(new THREE.CylinderGeometry(0.108, 0.108, 0.025, 8, 1, true), rockD, T).position.y = 0.3;
  for (let i = 0; i < 8; i++) {
    if (i === 2 || i === 3 || i === 6) continue;
    const a = (i / 8) * Math.PI * 2;
    const h = 0.05 + (i === 0 || i === 7 ? 0.07 : rng() * 0.03);
    const m = M(new THREE.BoxGeometry(0.05, h, 0.04), masonry, T);
    m.position.set(Math.cos(a) * 0.095, 0.34 + h / 2, Math.sin(a) * 0.095);
    m.rotation.y = -a;
  }
  for (let i = 0; i < 4; i++) {
    const a = rng() * 6.3, y = 0.06 + rng() * 0.24;
    const m = M(new THREE.BoxGeometry(0.045, 0.025, 0.02), rockD, T);
    m.position.set(Math.cos(a) * 0.112, y, Math.sin(a) * 0.112);
    m.rotation.y = -a + Math.PI / 2;
  }
  const win = M(new THREE.BoxGeometry(0.028, 0.05, 0.012), ember, T);
  win.position.set(0, 0.2, 0.108);
  const winGlow = glowSprite(T, '#ff9a5a', awake ? 0.3 : 0.2, V(0, 0.2, 0.13), 0.75, true);
  patch(T, moss, 0.05, 0.06, 0.07, 0.08, 1.0);
  patch(T, mossL, -0.07, 0.2, 0.06, 0.05, 1.4);
  // The garrison's banner, rotted to a rag on a crooked pole.
  limb(T, V(0.06, 0.32, -0.04), V(0.1, 0.64, -0.06), 0.008, 0.006, root, 4);
  const fl = panel(T, V(0.13, 0.62, -0.06), 0.13, 0.14, 0.02, 1, flag);
  fl.rotation.set(0, Math.PI / 2 + 0.3, 0.12);

  /* ---------- head: a great old face thrust forward — heavy brow, stone lids, broad nose, a beard of moss and roots ---------- */
  const headB = bj(chest, V(0, 1.98, 0.14), 'head');
  const head = headB.abs;
  // Skull and face (shadow-flesh), the crown and brow turned to rock.
  swell(head, flesh, 0, 2.15, 0.33, 0.21, 0.24, 0.22);
  lump(head, rock, 0, 2.29, 0.27, 0.21, 0.12, 0.21, 0.4, 1);
  // The brow: two heavy ridges of stone meeting in a frown over the nose.
  for (const k of [-1, 1]) {
    const br = lump(head, rock, k * 0.1, 2.215, 0.5, 0.12, 0.07, 0.09, 0.1);
    br.rotation.z = k * 0.25;
  }
  patch(head, moss, -0.03, 2.38, 0.25, 0.16);
  patch(head, mossL, 0.06, 2.4, 0.28, 0.08);
  patch(head, lichen, 0.12, 2.3, 0.44, 0.05, 1.0);
  for (const k of [-1, 1]) {
    swell(head, flesh, k * 0.12, 2.06, 0.45, 0.075, 0.065, 0.07);
    swell(head, flesh, k * 0.215, 2.15, 0.32, 0.035, 0.085, 0.06);
  }
  // A broad, broken nose.
  const nb = lump(head, rock, 0, 2.1, 0.55, 0.05, 0.085, 0.05, 0.1);
  nb.rotation.x = -0.25;
  swell(head, flesh, 0, 2.035, 0.56, 0.08, 0.045, 0.055);
  // The mouth: a dark gap, the upper lip and two stubs of teeth.
  swell(head, fleshD, 0, 1.97, 0.47, 0.11, 0.05, 0.06);
  swell(head, flesh, 0, 1.995, 0.51, 0.11, 0.025, 0.04);
  const eyes: THREE.Sprite[] = [];
  for (const k of [-1, 1]) {
    const at = V(k * 0.09, 2.15, 0.5);
    if (awake) {
      M(new THREE.SphereGeometry(0.04, 6, 4), ember, head).position.copy(at);
      // Heavy lids lifted halfway: a glare under the brow.
      const lid = M(new THREE.SphereGeometry(0.055, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), lidM, head);
      lid.position.copy(at).add(V(0, 0.004, -0.008));
      lid.rotation.x = -0.75;
      eyes.push(glowSprite(head, '#ff5a32', 0.32, at.clone().add(V(0, 0, 0.035)), 0.95, true));
    } else {
      // Shut: great bulging lids of pale stone, the seam between them barely smouldering.
      swell(head, lidM, at.x, at.y, at.z, 0.058, 0.045, 0.04);
      const sl = M(new THREE.BoxGeometry(0.09, 0.007, 0.01), ember, head);
      sl.position.copy(at).add(V(0, -0.012, 0.035));
      sl.rotation.z = k * 0.2;
    }
  }
  crack(head, [V(-0.16, 2.3, 0.42), V(-0.14, 2.22, 0.5), V(-0.17, 2.1, 0.5), V(-0.13, 2.0, 0.47)], 0.008);
  // The jaw (slack when it sleeps), and a long beard of moss and hanging roots.
  const jawB = bj(head, V(0, 2.02, 0.32), 'jaw');
  const jaw = jawB.abs;
  swell(jaw, flesh, 0, 1.93, 0.45, 0.15, 0.065, 0.12);
  for (const [x, h] of [[-0.09, 0.07], [0.085, 0.05]] as const) {
    const tth = M(new THREE.ConeGeometry(0.02, h, 4), masonry, jaw);
    tth.position.set(x, 1.95 + h / 2, 0.52);
    tth.rotation.set(-0.15, 0.4, -x * 2);
  }
  lump(jaw, moss, 0, 1.84, 0.47, 0.17, 0.12, 0.1, 0.3);
  lump(jaw, moss, -0.02, 1.71, 0.47, 0.12, 0.1, 0.08, 0.3);
  lump(jaw, mossL, 0.03, 1.62, 0.47, 0.07, 0.07, 0.06, 0.3);
  patch(jaw, mossL, 0.05, 1.86, 0.56, 0.07, 1.4);
  for (let i = 0; i < 4; i++) {
    const x = (i - 1.5) * 0.07;
    const len = 0.3 + rng() * 0.2 - Math.abs(x) * 0.8;
    rootTube(jaw, [V(x, 1.88, 0.5), V(x * 1.1 + (rng() - 0.5) * 0.05, 1.88 - len * 0.5, 0.52), V(x * 0.7 + (rng() - 0.5) * 0.06, 1.88 - len, 0.48)], 0.012);
  }
  // A mane: roots trailing from the back of the skull down the neck.
  for (let i = 0; i < 2; i++) {
    const x = (i - 0.5) * 0.18;
    rootTube(head, [V(x, 2.3, 0.15), V(x * 1.3, 2.12, 0.06), V(x * 1.4, 1.95, 0.02)], 0.013);
  }
  const mouthGlow = awake ? glowSprite(jaw, '#ff6a3a', 0.4, V(0, 1.97, 0.52), 0.5, true) : null;

  /* ---------- arms: long and heavy, shadow-flesh with patches of rock; hands that hang (asleep) or clench (awake) ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const side = k < 0 ? 'L' : 'R';
    const armB = bj(chest, V(k * 0.56, 1.82, 0), `arm${side}`);
    const sh = armB.abs;
    swell(sh, flesh, k * 0.58, 1.79, 0, 0.17, 0.16, 0.17);
    limb(sh, V(k * 0.58, 1.76, 0), V(k * 0.64, 1.32, 0.02), 0.14, 0.11, flesh, 7);
    swell(sh, flesh, k * 0.61, 1.55, 0.05, 0.13, 0.19, 0.13);
    if (k > 0) {
      lump(sh, rock, k * 0.64, 1.88, -0.01, 0.17, 0.1, 0.16);
      patch(sh, moss, k * 0.63, 1.96, -0.02, 0.14);
      patch(sh, mossL, k * 0.6, 1.98, 0.02, 0.07);
    }
    crack(sh, [V(k * 0.6, 1.7, 0.16), V(k * 0.63, 1.55, 0.17), V(k * 0.62, 1.42, 0.13)]);
    const foreB = bj(sh, V(k * 0.64, 1.3, 0.02), `fore${side}`);
    const fo = foreB.abs;
    limb(fo, V(k * 0.64, 1.3, 0.02), V(k * 0.66, 0.85, 0.04), 0.12, 0.09, flesh, 7);
    swell(fo, flesh, k * 0.65, 1.14, 0.03, 0.13, 0.19, 0.13);
    lump(fo, rock, k * 0.72, 1.06, 0.02, 0.06, 0.13, 0.08);
    patch(fo, lichen, k * 0.75, 1.1, 0.03, 0.04, 0, k * 1.3);
    if (k < 0) {
      // The broken shackle on the left wrist, its chain hanging plumb.
      const sk = M(new THREE.TorusGeometry(0.115, 0.03, 4, 10), iron, fo);
      sk.rotation.x = Math.PI / 2;
      sk.position.set(k * 0.66, 0.92, 0.04);
      const chainB = bj(fo, V(k * 0.66, 0.9, 0.16), 'chain');
      chainB.p.rotation.set(-(rx('body') + rx('chest') + rx('armL') + rx('foreL')), 0, -(rz('body') + rz('chest') + rz('armL') + rz('foreL')));
      for (let i = 0; i < 5; i++) {
        const l = M(new THREE.TorusGeometry(0.04, 0.012, 3, 5, i === 4 ? Math.PI * 1.3 : Math.PI * 2), iron, chainB.abs);
        l.position.set(k * 0.66, 0.86 - i * 0.07, 0.16);
        l.rotation.set(0, i % 2 ? Math.PI / 2 : 0, Math.PI / 2);
      }
      arms.chain = chainB.p;
    }
    // The hand: palm faces back (down when the arm is raised), fingers curl backwards.
    const handB = bj(fo, V(k * 0.66, 0.83, 0.04), `hand${side}`);
    const ha = handB.abs;
    swell(ha, flesh, k * 0.66, 0.75, 0.04, 0.1, 0.1, 0.06);
    const a1 = awake ? 1.3 : 0.35, a2 = awake ? 2.5 : 0.85;
    for (let i = 0; i < 4; i++) {
      const x = k * 0.66 + (i - 1.5) * 0.045;
      const p0 = V(x, 0.68, 0.04);
      const p1 = p0.clone().add(V(0, -Math.cos(a1) * 0.085, -Math.sin(a1) * 0.085));
      const p2 = p1.clone().add(V(0, -Math.cos(a2) * 0.075, -Math.sin(a2) * 0.075));
      limb(ha, p0, p1, 0.026, 0.024, flesh, 3);
      limb(ha, p1, p2, 0.023, 0.018, flesh, 3);
    }
    limb(ha, V(k * 0.58, 0.75, 0.02), V(k * 0.57, 0.66, -0.04), 0.028, 0.022, flesh, 4);
    arms[`arm${side}`] = armB.p;
    arms[`fore${side}`] = foreB.p;
    arms[`hand${side}`] = handB.p;
    if (k > 0) {
      // The boulder it throws, held under the right palm (the clips show it only while it is in hand).
      const rockB = bj(ha, V(0.66, 0.6, -0.08), 'rock');
      lump(rockB.abs, rock, 0.66, 0.6, -0.1, 0.16, 0.14, 0.15, 0.5, 0);
      patch(rockB.abs, mossL, 0.69, 0.71, -0.08, 0.08);
      arms.rock = rockB.p;
    }
  }

  /* ---------- smoke boiling off it, and (awake) the fire inside ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, y, z, h] of [[0.55, 1.98, -0.12, 0.75], [-0.1, 1.95, -0.48, 0.9]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const motes: THREE.Mesh[] = [];
  let core: THREE.Sprite | null = null;
  let L: THREE.PointLight;
  if (awake) {
    core = glowSprite(chest, '#ff4f3a', 0.8, V(0, 1.62, 0.5), 0.55, true);
    glowSprite(g, '#ff4f3a', 3.0, V(0, 1.3, 0.1), 0.1);
    for (let i = 0; i < 6; i++) {
      const m = M(new THREE.TetrahedronGeometry(0.018 + rng() * 0.012, 0), ember, g);
      m.userData.a = rng() * 6.3;
      m.userData.r = 0.35 + rng() * 0.35;
      m.userData.o = rng();
      motes.push(m);
    }
    L = pointLight(c, 0xff6a3a, 0.5, 3, V(0, 1.55, 0.75));
  } else {
    L = pointLight(c, 0xff8a4a, 0.25, 1.5, V(-0.55, 2.25, 0.1));
  }

  // The motion is in the clips; the frames only boil (smoke, embers rising, the eyes, the heart and the window).
  const t = (n: number): void => {
    for (const r of wisps) r.step();
    const f = ((n * 7) % 10) / 10;
    winGlow.material.opacity = 0.55 + 0.35 * f;
    if (awake) {
      const pulse = 0.5 + ((n * 3) % 10) / 20;
      if (core) core.material.opacity = 0.4 + pulse * 0.35;
      for (const e of eyes) e.material.opacity = 0.8 + 0.2 * f;
      if (mouthGlow) mouthGlow.material.opacity = 0.35 + 0.25 * pulse;
      L.intensity = (0.35 + pulse * 0.3) * LIGHT_K;
      for (const m of motes) {
        const u = ((m.userData.o as number) + n * 0.09) % 1;
        const a = (m.userData.a as number) + n * 0.15;
        const r = m.userData.r as number;
        m.position.set(Math.cos(a) * r, 1.0 + u * 1.6, Math.sin(a) * r * 0.8);
        m.scale.setScalar(1 - u * 0.8);
        m.rotation.set(n * 0.7, n * 0.5, 0);
      }
    } else {
      L.intensity = (0.2 + 0.1 * f) * LIGHT_K;
    }
  };
  t(0);
  return done(c, 2.3, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, jaw: jawB.p, tower: towerB.p, cloak: cloakB.p,
    armL: arms.armL!, foreL: arms.foreL!, handL: arms.handL!, chain: arms.chain!,
    armR: arms.armR!, foreR: arms.foreR!, handR: arms.handR!, rock: arms.rock!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!, rag: ragB.p,
  }));
};
